import { Cable, CheckCircle2, ChevronRight, Code, Database, GitMerge, Puzzle, Zap } from "lucide-react";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ChatInterface } from "../components/chat/ChatInterface";
import { FeatureCard, ProductCard } from "../components/features/FeatureCard";
import { Footer } from "../components/layout/Footer";
import { Navbar } from "../components/layout/Navbar";
import { useLanguage } from "../context/LanguageContext";
import { useTranslation } from "../hooks/useTranslation";
import { AnalyticsEventName, buildApiAccessPath, trackApiAccessCta, trackPromotionalFunnelEvent } from "../lib/analytics";

const Index = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  // Initialize fade-in animations
  useEffect(() => {
    const fadeElements = document.querySelectorAll(".fade-in-section");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    fadeElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      fadeElements.forEach((element) => {
        observer.unobserve(element);
      });
    };
  }, []);

  const trackCtaClick = (eventName: AnalyticsEventName, surface: string, ctaName: string, funnelStage: string, destination: string) => {
    trackPromotionalFunnelEvent(eventName, {
      surface,
      cta_name: ctaName,
      funnel_stage: funnelStage,
      destination,
      result_status: "clicked",
    });
  };

  const trackApiAccessClick = (surface: string, ctaName: string) => {
    const destination = buildApiAccessPath(surface);
    trackApiAccessCta({ surface, ctaName, destination });
  };

  const heroValuePoints = [t("hero.valuePoint1"), t("hero.valuePoint2"), t("hero.valuePoint3")];
  const trustPoints = [
    { title: t("trust.point1.title"), description: t("trust.point1.description") },
    { title: t("trust.point2.title"), description: t("trust.point2.description") },
    { title: t("trust.point3.title"), description: t("trust.point3.description") },
  ];

  return (
    <>
      <Helmet>
        <title>{t("meta.home.title")}</title>
        <meta name="description" content={t("meta.home.description")} />
        <meta name="keywords" content={t("meta.home.keywords")} />
        <meta property="og:title" content={t("meta.home.title")} />
        <meta property="og:description" content={t("meta.home.description")} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gugudata.ai" />
        <meta property="og:image" content="https://www.gugudata.ai/og.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="GuGuData.AI" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t("meta.home.title")} />
        <meta name="twitter:description" content={t("meta.home.description")} />
        <meta name="twitter:image" content="https://www.gugudata.ai/og.png" />
        <link rel="canonical" href="https://www.gugudata.ai" />
        <html lang={language} />
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow">
          {/* Hero Section */}
          <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-white dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="fade-in-section">
                <div className="text-center mb-16">
                  <h1 className="text-4xl md:text-6xl font-bold mb-6 text-gray-900 dark:text-white leading-tight">{t("hero.title")}</h1>
                  <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-5xl mx-auto mb-8">{t("hero.subtitle")}</p>
                  <ul className="grid gap-3 md:grid-cols-3 max-w-5xl mx-auto mb-10 text-left">
                    {heroValuePoints.map((point) => (
                      <li key={point} className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-blue" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link
                      to={buildApiAccessPath("home_hero")}
                      onClick={() => trackApiAccessClick("home_hero", "request_api_access")}
                      className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white bg-brand-blue rounded-full shadow-lg hover:bg-opacity-90 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1"
                    >
                      {t("hero.cta")} <ChevronRight className="ml-2 h-5 w-5" />
                    </Link>
                    <Link
                      to="/document"
                      onClick={() => trackCtaClick("click_view_documentation", "home_hero", "view_documentation", "documentation", "/document")}
                      className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-brand-blue bg-blue-50 dark:bg-blue-900/30 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-all duration-300"
                    >
                      {t("hero.secondaryCta")} <ChevronRight className="ml-2 h-5 w-5" />
                    </Link>
                    <Link
                      to="/contact"
                      onClick={() => trackCtaClick("click_contact_sales", "home_hero", "contact_team", "contact", "/contact")}
                      className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-300"
                    >
                      {t("hero.tertiaryCta")} <ChevronRight className="ml-2 h-5 w-5" />
                    </Link>
                  </div>
                </div>
              </div>

              {/* Chat Interface */}
              <div id="chat" className="fade-in-section">
                <ChatInterface />
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section id="features" className="py-24 bg-gray-50 dark:bg-gray-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="fade-in-section">
                <div className="text-center mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">{t("features.title")}</h2>
                  <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">{t("features.subtitle")}</p>
                  <p className="text-lg text-gray-500 dark:text-gray-400 max-w-4xl mx-auto mt-4">
                    The Model Context Protocol (MCP) is revolutionizing how AI applications access and interact with data. By providing a standardized interface for data sources, MCP eliminates the
                    complexity of integrating multiple APIs and enables AI models to access real-time, contextual information seamlessly.
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                  <FeatureCard icon={<GitMerge className="w-8 h-8" />} title={t("features.card1.title")} description={t("features.card1.description")} />
                  <FeatureCard icon={<Cable className="w-8 h-8" />} title={t("features.card2.title")} description={t("features.card2.description")} />
                  <FeatureCard icon={<Database className="w-8 h-8" />} title={t("features.card3.title")} description={t("features.card3.description")} />
                </div>

                <div className="mt-16 grid md:grid-cols-2 gap-8">
                  <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8 transition-all duration-300 hover:shadow-2xl">
                    <div className="flex items-start mb-6">
                      <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg mr-4">
                        <Code className="w-8 h-8 text-brand-blue" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">{t("features.standardized.title")}</h3>
                        <p className="text-gray-600 dark:text-gray-300">{t("features.standardized.description")}</p>
                      </div>
                    </div>
                    <ul className="space-y-3 text-gray-600 dark:text-gray-300 ml-4">
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-brand-blue rounded-full mr-2"></span>
                        <span>{t("features.standardized.list.item1")}</span>
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-brand-blue rounded-full mr-2"></span>
                        <span>{t("features.standardized.list.item2")}</span>
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-brand-blue rounded-full mr-2"></span>
                        <span>{t("features.standardized.list.item3")}</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8 transition-all duration-300 hover:shadow-2xl">
                    <div className="flex items-start mb-6">
                      <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg mr-4">
                        <Puzzle className="w-8 h-8 text-brand-blue" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">{t("features.modular.title")}</h3>
                        <p className="text-gray-600 dark:text-gray-300">{t("features.modular.description")}</p>
                      </div>
                    </div>
                    <ul className="space-y-3 text-gray-600 dark:text-gray-300 ml-4">
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-brand-blue rounded-full mr-2"></span>
                        <span>{t("features.modular.list.item1")}</span>
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-brand-blue rounded-full mr-2"></span>
                        <span>{t("features.modular.list.item2")}</span>
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-brand-blue rounded-full mr-2"></span>
                        <span>{t("features.modular.list.item3")}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Products Section */}
          <section id="products" className="py-24 bg-white dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="fade-in-section">
                <div className="text-center mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">{t("products.title")}</h2>
                  <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">{t("products.subtitle")}</p>
                  <p className="text-lg text-gray-500 dark:text-gray-400 max-w-4xl mx-auto mt-4">
                    Our comprehensive MCP solutions are designed to meet the diverse needs of modern AI applications, from startups to enterprise-level deployments. Whether you need real-time
                    financial data, educational resources, or specialized knowledge bases, our platform provides the tools and infrastructure to enhance your AI capabilities.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-12">
                  <ProductCard
                    icon={<Zap className="w-full h-full" />}
                    title={t("products.card1.title")}
                    description={t("products.card1.description")}
                    ctaText={t("products.card1.cta")}
                    ctaLink="/document"
                    onCtaClick={() => trackCtaClick("click_view_documentation", "products_section", "view_documentation", "documentation", "/document")}
                  />
                  <ProductCard
                    icon={<Database className="w-full h-full" />}
                    title={t("products.card2.title")}
                    description={t("products.card2.description")}
                    ctaText={t("products.card2.cta")}
                    ctaLink="/products#connectors"
                    onCtaClick={() => trackCtaClick("click_explore_connectors", "products_section", "explore_connectors", "connector_discovery", "/products#connectors")}
                  />
                </div>

                {/* MCP Integration Process */}
                <div className="mt-24">
                  <h3 className="text-2xl font-bold text-center mb-12 text-gray-900 dark:text-white">{t("features.integration.title")}</h3>
                  <div className="relative">
                    <div className="absolute top-1/2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-brand-blue to-transparent transform -translate-y-1/2 opacity-20" />
                    <div className="grid md:grid-cols-3 gap-8 relative">
                      {[
                        {
                          step: 1,
                          title: t("features.integration.step1.title"),
                          description: t("features.integration.step1.description"),
                        },
                        {
                          step: 2,
                          title: t("features.integration.step2.title"),
                          description: t("features.integration.step2.description"),
                        },
                        {
                          step: 3,
                          title: t("features.integration.step3.title"),
                          description: t("features.integration.step3.description"),
                        },
                      ].map((item, index) => (
                        <div key={item.step} className="relative bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg group hover:shadow-xl transition-all duration-300">
                          <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-brand-blue to-blue-400 rounded-xl flex items-center justify-center text-white font-bold text-lg transform group-hover:scale-110 transition-transform duration-300">
                            {item.step}
                          </div>
                          <div className="mt-4">
                            <h4 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">{item.title}</h4>
                            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{item.description}</p>
                          </div>
                          {index < 2 && (
                            <div className="hidden md:block absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 text-brand-blue">
                              <ChevronRight className="w-8 h-8 opacity-50" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 bg-brand-blue">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-3xl font-bold text-white mb-6">{t("features.cta.title")}</h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-8">{t("features.cta.description")}</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to={buildApiAccessPath("home_bottom_cta")}
                  onClick={() => trackApiAccessClick("home_bottom_cta", "request_api_access")}
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-brand-blue bg-white rounded-full shadow-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-xl"
                >
                  {t("hero.cta")} <ChevronRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/document"
                  onClick={() => trackCtaClick("click_view_documentation", "home_bottom_cta", "view_documentation", "documentation", "/document")}
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white border-2 border-white rounded-full shadow-lg hover:bg-white hover:text-brand-blue transition-all duration-300 hover:shadow-xl"
                >
                  {t("hero.secondaryCta")} <ChevronRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/contact"
                  onClick={() => trackCtaClick("click_contact_sales", "home_bottom_cta", "contact_team", "contact", "/contact")}
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white border-2 border-white rounded-full shadow-lg hover:bg-white hover:text-brand-blue transition-all duration-300 hover:shadow-xl"
                >
                  {t("hero.tertiaryCta")} <ChevronRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </section>

          {/* External Resources Section */}
          <section className="py-16 bg-gray-50 dark:bg-gray-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="fade-in-section">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">{t("features.external_resources.title")}</h2>
                  <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">{t("features.external_resources.subtitle")}</p>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                  <a
                    href="https://spec.modelcontextprotocol.io/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  >
                    <div className="flex items-start mb-6">
                      <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg mr-4">
                        <Code className="w-8 h-8 text-brand-blue" />
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white group-hover:text-brand-blue transition-colors">{t("features.external_resources.mcp_spec")}</h3>
                        <p className="text-gray-600 dark:text-gray-300">{t("features.external_resources.mcp_spec_desc")}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-brand-blue transition-colors" />
                    </div>
                  </a>

                  <a
                    href="https://github.com/modelcontextprotocol"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  >
                    <div className="flex items-start mb-6">
                      <div className="flex-shrink-0 bg-gray-100 dark:bg-gray-700 p-3 rounded-lg mr-4">
                        <GitMerge className="w-8 h-8 text-gray-700 dark:text-gray-300" />
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white group-hover:text-brand-blue transition-colors">{t("features.external_resources.github_samples")}</h3>
                        <p className="text-gray-600 dark:text-gray-300">{t("features.external_resources.github_samples_desc")}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-brand-blue transition-colors" />
                    </div>
                  </a>

                  <a
                    href="https://github.com/modelcontextprotocol/servers"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  >
                    <div className="flex items-start mb-6">
                      <div className="flex-shrink-0 bg-green-100 dark:bg-green-900/30 p-3 rounded-lg mr-4">
                        <Database className="w-8 h-8 text-green-600" />
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white group-hover:text-brand-blue transition-colors">{t("features.external_resources.community")}</h3>
                        <p className="text-gray-600 dark:text-gray-300">{t("features.external_resources.community_desc")}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-brand-blue transition-colors" />
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Trust Section */}
          <section className="py-16 bg-white dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="fade-in-section">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">{t("trust.title")}</h2>
                  <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">{t("trust.subtitle")}</p>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                  {trustPoints.map((point) => (
                    <article key={point.title} className="rounded-2xl border border-gray-200 bg-gray-50 p-8 dark:border-gray-800 dark:bg-gray-950">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">{point.title}</h3>
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{point.description}</p>
                    </article>
                  ))}
                </div>

                <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                      <a
                        href="/document"
                        onClick={() => trackCtaClick("click_view_documentation", "support_section", "view_documentation", "documentation", "/document")}
                        className="inline-flex items-center px-6 py-3 text-sm font-medium text-brand-blue bg-blue-50 dark:bg-blue-900/30 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                      >
                        {t("hero.secondaryCta")}
                      </a>
                      <a
                        href="/contact"
                        onClick={() => trackCtaClick("click_contact_sales", "support_section", "contact_support", "contact", "/contact")}
                        className="inline-flex items-center px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                      >
                        {t("hero.tertiaryCta")}
                      </a>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Index;
